import React, { useState, useRef, useEffect } from "react";

type PopoverPosition = "top" | "bottom" | "left" | "right";

interface PopoverProps {
  trigger?: React.ReactNode;
  children: React.ReactNode;
  position?: PopoverPosition;
  showProp?: boolean;
}

const Popover: React.FC<PopoverProps> = ({
  trigger,
  children,
  position = "bottom",
  showProp,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [popoverPosition, setPopoverPosition] =
    useState<PopoverPosition>(position);
  const triggerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    setIsOpen(showProp || false);
  }, [showProp]);

  useEffect(() => {
    if (isOpen && triggerRef.current && popoverRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const popoverRect = popoverRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      let newPosition = position;

      // Check if popover fits in the preferred position
      if (
        position === "bottom" &&
        triggerRect.bottom + popoverRect.height > viewportHeight
      ) {
        newPosition = "top";
      } else if (
        position === "top" &&
        triggerRect.top - popoverRect.height < 0
      ) {
        newPosition = "bottom";
      } else if (
        position === "right" &&
        triggerRect.right + popoverRect.width > viewportWidth
      ) {
        newPosition = "left";
      } else if (
        position === "left" &&
        triggerRect.left - popoverRect.width < 0
      ) {
        newPosition = "right";
      }

      setPopoverPosition(newPosition);
    }
  }, [isOpen, position]);

  const getPopoverStyles = (): string => {
    const baseStyles =
      "absolute z-50 bg-white rounded-lg shadow-lg border border-gray-200 p-4 min-w-60 max-w-xs";

    const positions: Record<PopoverPosition, string> = {
      bottom: "top-full mt-2  right-0",
      top: "bottom-full mb-7 left-1/2 -translate-x-1/2",
      right: "left-full ml-2 top-1/2 -translate-y-1/2",
      left: "right-full mr-2 top-1/2 -translate-y-1/2",
    };

    return `${baseStyles} ${positions[popoverPosition]}`;
  };

  const getArrowStyles = (): string => {
    const baseStyles = "absolute w-3 h-3 bg-white border transform rotate-45";

    const positions: Record<PopoverPosition, string> = {
      bottom: "-top-1.5  right-1  border-b-0 border-r-0 border-gray-200",
      top: "-bottom-1.5 left-1/2 -translate-x-1/2 border-t-0 border-l-0 border-gray-200",
      right:
        "-left-1.5 top-1/2 -translate-y-1/2 border-t-0 border-r-0 border-gray-200",
      left: "-right-1.5 top-1/2 -translate-y-1/2 border-b-0 border-l-0 border-gray-200",
    };

    return `${baseStyles} ${positions[popoverPosition]}`;
  };

  return (
    <div className="relative inline-block">
      <div>{trigger}</div>

      {isOpen && (
        <div ref={popoverRef} className={getPopoverStyles()}>
          <div
            className={getArrowStyles()}
            onClick={() => setIsOpen(!isOpen)}
          />
          {children}
        </div>
      )}
    </div>
  );
};

export default Popover;
